import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Crown, ShieldCheck, TrendingUp } from 'lucide-react';
import { NavLink } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';

export function getLimitState({ used = 0, limit = null, percentUsed = null }) {
  const isUnlimited = limit === null || limit === undefined || limit === -1;
  const rawPercent = percentUsed ?? (isUnlimited || Number(limit) <= 0 ? 0 : (Number(used || 0) / Number(limit)) * 100);
  const percent = Math.max(0, Number(rawPercent || 0));

  if (isUnlimited) return { key: 'unlimited', label: 'Unlimited', percent: 0 };
  if (percent >= 100) return { key: 'exceeded', label: 'At Limit', percent };
  if (percent >= 90) return { key: 'critical', label: 'Critical', percent };
  if (percent >= 80) return { key: 'warning', label: 'Near Limit', percent };
  return { key: 'normal', label: 'Healthy', percent };
}

export function getLimitIndicatorClass(stateKey) {
  if (stateKey === 'exceeded') return 'bg-destructive';
  if (stateKey === 'critical') return 'bg-orange-500';
  if (stateKey === 'warning') return 'bg-warning';
  if (stateKey === 'unlimited') return 'bg-emerald-500';
  return 'bg-emerald-500';
}

export function defaultLimitFormatter(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-US').format(number);
}

export function tokenLimitFormatter(value) {
  if (value === null || value === undefined || value === -1) return 'Unlimited';
  const number = Number(value || 0);
  if (number >= 1000000) return `${(number / 1000000).toFixed(number >= 10000000 ? 0 : 1)}M`;
  if (number >= 1000) return `${(number / 1000).toFixed(number >= 10000 ? 0 : 1)}K`;
  return defaultLimitFormatter(number);
}

function statusBadge(state, displayType = 'metered') {
  if (state.key === 'not_configured') {
    return (
      <Badge variant="outline" className="border-amber-300 bg-amber-500/10 text-amber-700 dark:text-amber-300">
        <AlertTriangle className="mr-1 h-3 w-3" />
        Not configured
      </Badge>
    );
  }
  if (displayType === 'rule') {
    return (
      <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">
        <ShieldCheck className="mr-1 h-3 w-3" />
        Plan rule
      </Badge>
    );
  }
  if (state.key === 'unlimited') {
    return (
      <Badge variant="outline" className="border-purple-200 bg-purple-500/10 text-purple-600">
        <Crown className="mr-1 h-3 w-3" />
        Unlimited
      </Badge>
    );
  }
  if (displayType === 'capacity' && state.key === 'normal') {
    return (
      <Badge variant="outline" className="border-blue-200 bg-blue-500/10 text-blue-600">
        <CheckCircle2 className="mr-1 h-3 w-3" />
        Capacity
      </Badge>
    );
  }
  if (state.key === 'exceeded') {
    return (
      <Badge variant="outline" className="border-destructive/30 bg-destructive/10 text-destructive">
        <AlertTriangle className="mr-1 h-3 w-3" />
        At Limit
      </Badge>
    );
  }
  if (state.key === 'critical' || state.key === 'warning') {
    return (
      <Badge variant="outline" className="border-warning/30 bg-warning/10 text-warning">
        <TrendingUp className="mr-1 h-3 w-3" />
        Near Limit
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-emerald-200 bg-emerald-500/10 text-emerald-600">
      <CheckCircle2 className="mr-1 h-3 w-3" />
      Healthy
    </Badge>
  );
}

export function LimitMeter({
  title,
  used = 0,
  limit = null,
  limitDetail = null,
  icon: Icon,
  formatter = defaultLimitFormatter,
  percentUsed = null,
  caption,
  explanation,
  ctaLabel,
  ctaUrl,
  showManagePlanCta = false,
  managePlanCtaThreshold = 80,
  managePlanCtaLabel = 'Manage Plan',
  managePlanCtaUrl = '/settings/billing',
  className = '',
  compact = false,
  testId,
  isConfigured = true,
  displayType = 'metered',
}) {
  const { hasRole, user } = useAuth();
  const resolvedTitle = limitDetail?.label || title;
  const resolvedUsed = limitDetail?.current_usage ?? used;
  const resolvedLimit = limitDetail?.is_unlimited ? null : (limitDetail?.effective_limit ?? limit);
  const resolvedCaption = caption || limitDetail?.source_label;
  const resolvedConfigured = isConfigured && (limitDetail?.is_configured ?? true);
  const state = resolvedConfigured
    ? getLimitState({ used: resolvedUsed, limit: resolvedLimit, percentUsed })
    : { key: 'not_configured', label: 'Not configured', percent: 0 };
  const isUnlimited = state.key === 'unlimited';
  const isNotConfigured = state.key === 'not_configured';
  const shownPercent = Math.min(state.percent, 100);
  const remaining = isUnlimited ? null : Math.max(Number(resolvedLimit || 0) - Number(resolvedUsed || 0), 0);
  const isRule = displayType === 'rule';
  const isCapacity = displayType === 'capacity';
  const limitNoun = isCapacity ? 'capacity' : 'included';
  const canManagePlan = Boolean(hasRole?.(['admin']) || user?.is_superadmin);
  const shouldShowManagePlanCta = Boolean(
    showManagePlanCta
    && canManagePlan
    && !isRule
    && !isUnlimited
    && !isNotConfigured
    && state.percent >= managePlanCtaThreshold
  );
  const explicitCtaIsBilling = ctaUrl === managePlanCtaUrl || ctaUrl === '/settings/billing';
  const resolvedCtaUrl = shouldShowManagePlanCta
    ? managePlanCtaUrl
    : (ctaUrl && (!explicitCtaIsBilling || canManagePlan) ? ctaUrl : undefined);
  const resolvedCtaLabel = shouldShowManagePlanCta ? managePlanCtaLabel : (ctaLabel || 'Manage Plan');

  const body = (
    <>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          {Icon && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{resolvedTitle}</p>
            <p className="text-sm text-muted-foreground">
              {isNotConfigured
                ? 'Numeric limit required'
                : isRule
                  ? 'Action constraint'
                  : isUnlimited
                    ? 'Custom capacity'
                    : `${formatter(resolvedLimit)} ${limitNoun}`}
            </p>
          </div>
        </div>
        {!compact && statusBadge(state, displayType)}
      </div>

      {isRule ? (
        <div className="rounded-lg border bg-muted/20 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Maximum allowed</p>
          <p className="mt-2 text-3xl font-bold text-foreground">
            {isNotConfigured ? 'Not set' : isUnlimited ? 'Custom' : formatter(resolvedLimit)}
          </p>
          {!isNotConfigured && !isUnlimited && (
            <p className="mt-1 text-sm text-muted-foreground">Applied when this action is performed.</p>
          )}
        </div>
      ) : (
        <div className="mb-2 flex items-end justify-between gap-4">
          <p className="text-3xl font-bold text-foreground">{formatter(resolvedUsed || 0)}</p>
          {!isUnlimited && !isNotConfigured && (
            <p className="text-sm text-muted-foreground">
              {isCapacity
                ? `${formatter(resolvedUsed || 0)} of ${formatter(resolvedLimit)}`
                : `${formatter(resolvedUsed || 0)} / ${formatter(resolvedLimit)}`}
            </p>
          )}
        </div>
      )}

      {!isRule && !isUnlimited && !isNotConfigured && (
        <>
          <Progress
            value={shownPercent}
            className="h-2"
            indicatorClassName={getLimitIndicatorClass(state.key)}
          />
          <p className="mt-2 text-sm text-muted-foreground">
            {isCapacity
              ? `${formatter(remaining)} available`
              : `${Math.round(state.percent)}% used • ${formatter(remaining)} remaining`}
          </p>
        </>
      )}

      {!isRule && isUnlimited && (
        <p className="text-sm text-muted-foreground">No fixed cap applies to this plan.</p>
      )}

      {isRule && isUnlimited && (
        <p className="text-sm text-muted-foreground">This rule is configured outside the standard plan limits.</p>
      )}

      {isNotConfigured && (
        <p className="mt-3 text-sm text-muted-foreground">Configure this limit before the tenant can use this plan.</p>
      )}

      {(resolvedCaption || explanation || resolvedCtaUrl) && (
        <div className="mt-4 flex flex-col gap-3 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1 text-sm text-muted-foreground">
            {resolvedCaption && <p>{resolvedCaption}</p>}
            {explanation && <p>{explanation}</p>}
          </div>
          {resolvedCtaUrl && (
            <Button asChild variant={state.key === 'normal' ? 'outline' : 'default'} size="sm" className="shrink-0">
              <NavLink to={resolvedCtaUrl}>{resolvedCtaLabel}</NavLink>
            </Button>
          )}
        </div>
      )}
    </>
  );

  return (
    <Card className={className} data-testid={testId}>
      <CardContent className="p-4 sm:p-6">{body}</CardContent>
    </Card>
  );
}

export default LimitMeter;
